// External Imports
import { useContext, useEffect, useState } from "react";
import {
  MdCalendarMonth,
  MdHelpOutline,
  MdLogout,
  MdSearch,
} from "react-icons/md";

// Internal Imports
import { AuthContext } from "../../context/auth-context";
import { DataContext } from "../../context/data-context";
import PoshLogo from "../UI/PoshLogo";
import CheckOnline from "../UI/CheckOnline";
import ReminderMenu from "../UI/ReminderMenu";
import LoadingSpinner from "../UI/LoadingSpinner";
import { UtilityContext } from "../../context/util-context";

/** ========================================================================
 * * Navbar Component
 * This component renders the navigation bar at the top of the app.
 * ========================================================================== */

export default function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const { admin, loadingData } = useContext(DataContext);
  const { navigate } = useContext(UtilityContext);

  // State for ChatBot
  const [initialized, setInitialized] = useState(false);
  const [bot, setBot] = useState(false);

  /* ======================= HIDE CHATBOT ======================= */
  // Hide Chatbot if it is initialized
  const hideBot = () => {
    if (initialized) {
      const chatbaseButton = document.getElementById("chatbase-bubble-button");
      chatbaseButton.style.display = "none";
      const chatbaseWindow = document.getElementById("chatbase-bubble-window");
      chatbaseWindow.style.display = "none";
    }
  };

  /* ======================= SHOW CHATBOT ======================= */
  // Show Chatbot if it is initialized, otherwise initialize it
  const showBot = () => {
    if (initialized) {
      const chatbaseButton = document.getElementById("chatbase-bubble-button");
      chatbaseButton.style.display = "block";
      const chatbaseWindow = document.getElementById("chatbase-bubble-window");
      chatbaseWindow.style.display = "block";
    } else initializeChatbase();
  };

  /* ======================= INITIALIZE CHATBOT ======================= */
  const initializeChatbase = () => {
    // Initialize chatbase if it isn't already initialized
    if (!window.chatbase || window.chatbase("getState") !== "initialized") {
      window.chatbase = (...args) => {
        if (!window.chatbase.q) {
          window.chatbase.q = [];
        }
        window.chatbase.q.push(args);
      };
      window.chatbase = new Proxy(window.chatbase, {
        get(target, prop) {
          if (prop === "q") {
            return target.q;
          }
          return (...args) => target(prop, ...args);
        },
      });
    }

    const autoClickChatButton = () => {
      const intervalId = setInterval(() => {
        // Adjust the selector to match the Chatbase button element
        const chatButton = document.getElementById("chatbase-bubble-button");
        if (chatButton) {
          chatButton.click();
          clearInterval(intervalId);
        }
      }, 1000);
      // Optionally, stop polling after 10 seconds if button is not found
      setTimeout(() => clearInterval(intervalId), 10000);
    };

    // Function to load the Chatbase script
    const onLoad = () => {
      const script = document.createElement("script");
      script.src = "https://www.chatbase.co/embed.min.js";
      script.id = "Fy87Z2jX1NKJihumgH5UH";
      script.setAttribute("domain", "www.chatbase.co");
      document.body.appendChild(script);
    };

    // If the document is already loaded, run onLoad immediately; otherwise, wait for the window load event
    if (document.readyState === "complete") {
      onLoad();
      autoClickChatButton();
      setInitialized(true);
    } else {
      window.addEventListener("load", onLoad);
      // Return a cleanup function to remove the event listener when not needed anymore
      return () => window.removeEventListener("load", onLoad);
    }
  };

  // Handle Chatbot visibility
  useEffect(() => {
    if (bot) showBot();
    else hideBot();
  }, [bot]);

  return (
    <header className="z-20">
      <CheckOnline />

      <div className="mx-6 md:mx-12">
        <div className="flex items-center justify-between py-4">
          <div className="flex items-end justify-start space-x-4">
            <PoshLogo />
            <p className="text-2xl md:text-3xl font-extralight text-stone-400">
              Admin <span className="text-xs">v2.2.1</span>
            </p>
          </div>

          <div className="-my-2 md:hidden">
            <div className="flex items-center px-4 py-2 space-x-4 text-center rounded-md h-fit bg-stone-600">
              <MdCalendarMonth className="icon" onClick={(e) => navigate("/calendar", e)} />
              {/* <MdHelpOutline className="icon" onClick={() => setBot(!bot)} /> */}
              <MdSearch className="icon" onClick={(e) => navigate("/query", e)} />
              <ReminderMenu />
              <MdLogout onClick={() => logout()} className="icon" />
            </div>
          </div>

          {loadingData ? (
            <LoadingSpinner classes="w-6 h-6 text-white" />
          ) : (
            <div className="items-center justify-end hidden md:flex md:flex-1">
              {user && (
                <div className="flex items-center space-x-4">
                  {admin.photo && (
                    <img src={admin.photo} className="w-6 h-6 rounded-full" />
                  )}
                  <p className="text-sm font-semibold text-stone-200">
                    {admin?.name}
                  </p>

                  <div className="flex items-center px-4 py-2 space-x-4 text-center rounded-md h-fit bg-stone-600">
                    <MdCalendarMonth className="icon" onClick={(e) => navigate("/calendar", e)} />
                    {/* <MdHelpOutline
                      className="icon"
                      onClick={() => setBot(!bot)}
                    /> */}
                    <MdSearch className="icon" onClick={(e) => navigate("/query", e)} />
                    <ReminderMenu />
                    <MdLogout onClick={() => logout()} className="icon" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
